import {SubtitleFormat, SubtitleTrack, UserVideo} from "@/lib/types";
import {generateRandomFileName, uploadFileToStorage} from "@/lib/FileUpload";
import {getLanguageInfoByCode} from "@/lib/LanguageUtils";
import {firestore, functions, httpsCallable} from "@/firebase/firebaseConfig";
import {collection, getDocs, onSnapshot, query, updateDoc, where} from "@firebase/firestore";

export function uploadSubtitleWebVTT(file: File, languageCode: string, userVideo: UserVideo): Promise<SubtitleTrack> {

    //Validate the file is a WebVTT file

    //Check the file type is VTT
    if (file.type !== 'text/vtt') {
        return Promise.reject(new Error('Please upload a valid WebVTT file.'));
    }

    //Check the file starts with WEBVTT
    const reader = new FileReader();
    reader.onload = function (e) {
        console.log('Loading reader')
        const text = e?.target?.result || '';
        let convertedText: string = '';
        if (text instanceof ArrayBuffer) {
            // @ts-ignore
            convertedText = String.fromCharCode(...new Uint8Array(text))
        } else {
            convertedText = text
        }
        if (!convertedText.startsWith('WEBVTT')) {
            return Promise.reject(new Error('This file does not appear to be a valid WebVTT file.'));
        }
        // Further processing if needed
    };
    reader.readAsText(file);

    //Upload the subtitle file and save it in the database
    return uploadSubtitleTrack(file, languageCode, 'vtt', userVideo)
}

export function uploadSubtitleTrack(file: File, languageCode: string, format: SubtitleFormat, userVideo: UserVideo): Promise<SubtitleTrack> {
    const userVideoId = userVideo?.id

    if (!file) {
        console.error('No file selected. Upload cancelled.')
        return Promise.reject(new Error('No file selected. Upload cancelled.'))
    }
    if (!languageCode) {
        console.error('Language not selected. Upload cancelled.')
        return Promise.reject(new Error('Language not selected. Upload cancelled.'))
    }
    if (file.size > 2000000) {
        return Promise.reject(new Error('File size exceeds 2MB. Please choose a smaller file.'))
    }
    if (!userVideoId) {
        return Promise.reject(new Error('User video id not provided. Upload cancelled.'))
    }

    const fileExtension = file.name.split('.').pop()
    const fileType = file.type
    const fileName = generateRandomFileName(22, fileExtension)
    const filePath = `videos/${userVideoId}/subtitles/originals/${languageCode}/` + fileName

    return uploadFileToStorage(file, filePath, fileType).then((result) => {
        //Let's prepare an AudioTrack object which has all the info about the audio track
        //Then call a cloud function to save the AudioTrack object and process adding it to the video
        //Eventually we will want the API to be able to handle the upload as well.

        console.log('File uploaded to', result.downloadUrl)

        const languageLabel = getLanguageInfoByCode(languageCode)?.nativeLabel || languageCode

        const subtitleTrack: SubtitleTrack = {
            version: 1,
            userVideoId: userVideoId,
            format: format,
            url: result.downloadUrl,
            languageCode: languageCode,
            label: languageLabel,
            size: file.size,
            fileType: fileType,
            fileName: fileName,
            originalFileName: file?.name,
            storageFullPath: result.fileRef.fullPath,
            storageBucket: result.fileRef.bucket,
            storagePath: result.fileRef.name,
            dateCreated: new Date(),
            lastUpdated: new Date
        }

        return addSubtitleTrack(subtitleTrack)
    }).catch((error) => {
        console.error('Error uploading subtitle track', error)
        throw new Error('Error uploading subtitle track')
    })
}

export async function overwriteSubtitleWebVTT(file: File, format: SubtitleFormat, userVideo: UserVideo, fullPath: string): Promise<void> {
    const userVideoId = userVideo?.id

    if (!file) {
        console.error('No file selected. Upload cancelled.')
        return Promise.reject(new Error('No file selected. Upload cancelled.'))
    }
    if (file.size > 2000000) {
        return Promise.reject(new Error('File size exceeds 2MB. Please choose a smaller file.'))
    }
    if (!userVideoId) {
        return Promise.reject(new Error('User video id not provided. Upload cancelled.'))
    }

    const fileType = file.type
    const filePath = fullPath.substring(fullPath.indexOf("videos/"))

    const result = await uploadFileToStorage(file, filePath, fileType)
    // update new downloadUrl to firebase database for the subtitle track
    if (result && result.downloadUrl) {
        const subtitleCollection = collection(firestore, 'SubtitleTracks')
        return getDocs(query(subtitleCollection, where("karaokeVttStoragePath", "==", fullPath)))
            .then((results) => {
                if (results.empty) {
                    return getDocs(query(subtitleCollection, where("storageFullPath", "==", fullPath)))
                        .then((results) => {
                            if (results.empty) {
                                return Promise.reject(new Error('No subtitle track found for overwriting'))
                            }
                            results.forEach((doc) => {
                                updateDoc(doc.ref, {url: result.downloadUrl})
                            })
                        })
                } else {
                    results.forEach((doc) => {
                        updateDoc(doc.ref, {karaokeVttUrl: result.downloadUrl, url: result.downloadUrl})
                    })
                }
            })
    } else {
        return Promise.reject(new Error('Error uploading subtitle track for overwriting'))
    }
}

export function addSubtitleTrack(subtitleTrack: SubtitleTrack): Promise<SubtitleTrack> {
    const addSubtitleTrack = httpsCallable(functions, 'video-addSubtitleTrack');
    return addSubtitleTrack({subtitleTrack}).then((result) => {
        console.log('Called video-addSubtitleTrack with result', result)
        return subtitleTrack
    }).catch((error) => {
        console.error('Error creating subtitle track', error)
        throw new Error('Error creating subtitle track')
    })
}

export function removeSubtitleTrack(subtitleTrackId: string): Promise<void> {
    const removeSubtitleTrack = httpsCallable(functions, 'video-removeSubtitleTrack');
    return removeSubtitleTrack({subtitleTrackId})
        .then(() => {
            console.log('Removed subtitle track with ID', subtitleTrackId);
        })
        .catch((error) => {
            console.error('Error removing subtitle track', error);
            throw new Error('Error removing subtitle track');
        });
}

export function listSubtitleTracksForVideo(userVideoId: string, languageOrder?: string[]): Promise<SubtitleTrack[]> {
    return getDocs(query(collection(firestore, 'SubtitleTracks'), where("userVideoId", "==", userVideoId)))
        .then((results) => {
            const tracks: SubtitleTrack[] = []
            results.forEach((doc) => {
                const track = {...doc.data(), id: doc.id} as SubtitleTrack//mapSubtitleTrack(doc)
                if (track) {
                    tracks.push(track)
                }
            })
            console.log('Subtitle Track language order', languageOrder)

            if (languageOrder) {
                // Go through the tracks list and see if there are any tracks that are not in the languageOrder array
                // If so then add the languageCode to an array called unSortedLanguages
                const unsortedLanguages: string[] = []
                tracks.forEach((track) => {
                    if (languageOrder.indexOf(track.languageCode) === -1) {
                        unsortedLanguages.push(track.languageCode)
                    }
                })

                // Now sort the unsorted language codes alphabetically
                unsortedLanguages.sort()

                // Append unsortedLanguages to end of languageOrder
                languageOrder.push(...unsortedLanguages)

                tracks.sort((a: SubtitleTrack, b: SubtitleTrack) => {
                    if (languageOrder.indexOf(b.languageCode) === -1) return -1;
                    if (languageOrder.indexOf(a.languageCode) === -1) return 1;
                    return languageOrder.indexOf(a.languageCode) - languageOrder.indexOf(b.languageCode);
                })
            }
            console.log('Returning subtitle tracks', tracks)
            return tracks
        })
}


// Listener for changes to SubtitleTracks for a video
export function subscribeToSubtitleTracksChanges(userVideoId: string, callback: () => void): () => void {
    const q = query(collection(firestore, 'SubtitleTracks'), where("userVideoId", "==", userVideoId))
    return onSnapshot(q, (snapshot) => {
        console.log('Subtitle Tracks Snapshot', snapshot)
        callback && callback()
    })
}

/**
 * Executes the process of regenerating the subtitle track based off the audio for the specified language
 * @param userVideoId
 * @param languageCode
 */
export function retranscribeAudio(userVideoId: string, languageCode: string): Promise<void> {
    const removeSubtitleTrack = httpsCallable(functions, 'transcription-retranscribe');
    return removeSubtitleTrack({userVideoId, languageCode})
        .then(() => {
            console.log(`Regenerating the subtitle track, userVideoId: ${userVideoId}, languageCode: ${languageCode}` );
        })
        .catch((error) => {
            console.log(`Error regenerating the subtitle track, userVideoId: ${userVideoId}, languageCode: ${languageCode}`, error );
            throw new Error('Error regenerating subtitle track');
        });
}